import { API_BASE_URL } from '../api/config';
import { sleepMs } from '../api/config';
import { BRAND_NAME } from '../api/config';

const escapeHtml = (v) =>
  String(v ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

function getRecordYear(rec) {
  const y = String(rec?.year ?? '').trim();
  if (/^\d{4}$/.test(y)) return y;
  const date = String(rec?.date ?? '').trim();
  const dateTail = date.match(/(\d{4})\s*$/);
  if (dateTail) return dateTail[1];
  const isoDate = date.match(/^(\d{4})-/);
  if (isoDate) return isoDate[1];
  const docYear = String(rec?.document_number ?? '').trim().match(/\/(\d{4})$/);
  if (docYear) return docYear[1];
  return '—';
}

function isEmptyYearMarker(rec) {
  if (!rec || typeof rec !== 'object') return false;
  if (rec.no_documents === true || rec.has_documents === false) return true;
  if (String(rec.search_status || '').trim() === 'no_records') return true;
  const year = getRecordYear(rec);
  if (year === '—') return false;
  const hasDocContent = Boolean(
    String(rec.document_number ?? '').trim() ||
    String(rec.document_type ?? '').trim() ||
    String(rec.text ?? '').trim() ||
    String(rec.seller_party ?? '').trim() ||
    String(rec.buyer_party ?? '').trim() ||
    String(rec.pdf_link ?? '').trim()
  );
  return !hasDocContent;
}

function groupRecordsByYear(records) {
  const groups = new Map();
  const ensureYear = (year) => {
    if (!groups.has(year)) groups.set(year, []);
    return groups.get(year);
  };
  for (const rec of records || []) {
    const year = getRecordYear(rec);
    if (isEmptyYearMarker(rec)) {
      ensureYear(year);
      continue;
    }
    ensureYear(year).push(rec);
  }
  const yearSortKey = (y) => (y === '—' ? -1 : parseInt(y, 10) || 0);
  return [...groups.entries()].sort((a, b) => yearSortKey(b[0]) - yearSortKey(a[0]));
}

const YEAR_EMPTY_MESSAGE = 'Zero document registered';

export function generateHtmlReport({ records, ctx }) {
  const safe = (v) => {
    const s = String(v ?? '').trim();
    return s || '—';
  };
  const locDistrict = safe(ctx.district || ctx.de);
  const locTaluka = safe(ctx.taluka || ctx.te);
  const locVillage = safe(ctx.village || ctx.ve);

  const wrapLines = (text, maxLen) => {
    const s = String(text || '').replace(/\s+/g, ' ').trim();
    if (!s) return ['—'];
    const words = s.split(' ');
    const lines = [];
    let line = '';
    for (const w of words) {
      const next = line ? `${line} ${w}` : w;
      if (next.length <= maxLen) line = next;
      else {
        if (line) lines.push(line);
        line = w;
      }
    }
    if (line) lines.push(line);
    return lines.length ? lines : ['—'];
  };

  const rowPairs = (rec) => [
    ['Document Number', rec.document_number],
    ['Document Type', rec.document_type],
    ['Registration Office', rec.registration_office],
    ['Date of Registration', rec.date],
    ['Seller / Executor Party', rec.seller_party],
    ['Buyer / Claimant Party', rec.buyer_party],
    ['Property Description', rec.text],
    ['District', locDistrict],
    ['Taluka', locTaluka],
    ['Village', locVillage],
  ];

  const renderRecordTable = (rec) => {
    const rows = rowPairs(rec || {}).map(([k, val]) => {
      const lines = wrapLines(val, 92);
      const valueHtml = lines.map((ln) => `<div>${escapeHtml(ln)}</div>`).join('');
      return `<tr><th>${escapeHtml(k)}</th><td>${valueHtml}</td></tr>`;
    }).join('');
    return `<section class="record"><table>${rows}</table></section>`;
  };

  const recordsHtml = groupRecordsByYear(records).map(([year, yearRecords]) => {
    const body = yearRecords.length
      ? yearRecords.map((rec, idx) =>
          `${idx > 0 ? '<hr class="record-separator"/>' : ''}${renderRecordTable(rec)}`
        ).join('\n')
      : `<p class="year-empty">${escapeHtml(YEAR_EMPTY_MESSAGE)}</p>`;
    return `<section class="year-group"><h2>${escapeHtml(year)}</h2>${body}</section>`;
  }).join('\n');

  const generatedOn = escapeHtml(new Date().toLocaleString('en-IN'));
  const query = escapeHtml(safe(ctx.query));
  const title = 'Property Search Report';

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8"/>
<title>${escapeHtml(title)} — ${escapeHtml(BRAND_NAME)}</title>
<style>
  :root { --primary:#1B4332; --accent:#C1440E; --muted:#5c6b63; --border:#E8E0D4; --bg:#FAF7F2; }
  body { font-family: 'Work Sans', system-ui, sans-serif; margin:0; padding:28px; background:#fff; color:#1A1A1A; line-height:1.5; }
  .wrap { max-width:880px; margin:0 auto; }
  header { border-bottom:3px solid var(--primary); padding-bottom:18px; margin-bottom:22px; }
  h1 { margin:0 0 4px; font-family: Georgia, serif; font-size:1.75rem; color:var(--primary); }
  .sub { margin:0; font-size:0.9rem; color:var(--muted); }
  .ctx { background:var(--bg); border:1px solid var(--border); padding:14px 18px; margin-bottom:24px; display:grid; grid-template-columns:1fr 1fr; gap:8px 16px; font-size:0.9rem; }
  .ctx div:nth-child(odd) { font-weight:700; color:var(--primary); }
  .year-group { margin-bottom:28px; }
  .year-group h2 { margin:0; font-size:1rem; background:var(--primary); color:#fff; padding:10px 14px; }
  table { width:100%; border-collapse:collapse; font-size:0.86rem; border:1px solid var(--border); }
  th { text-align:left; width:34%; background:var(--bg); border:1px solid var(--border); padding:10px 12px; font-weight:600; color:var(--primary); }
  td { border:1px solid var(--border); padding:10px 12px; }
  .year-empty { margin:0; padding:14px; border:1px solid var(--border); border-top:none; background:var(--bg); color:var(--muted); font-weight:600; }
  .record-separator { border:0; border-top:2px solid var(--border); margin:18px 0; }
  footer { margin-top:28px; padding-top:12px; border-top:1px solid var(--border); font-size:0.75rem; color:var(--muted); }
</style>
</head>
<body>
<div class="wrap">
  <header>
    <h1>${escapeHtml(title)}</h1>
    <p class="sub">${escapeHtml(BRAND_NAME)} — Registry Search Report</p>
  </header>
  <div class="ctx">
    <div>District</div><div>${escapeHtml(locDistrict)}</div>
    <div>Taluka</div><div>${escapeHtml(locTaluka)}</div>
    <div>Village</div><div>${escapeHtml(locVillage)}</div>
    <div>Property No.</div><div>${query}</div>
    <div>Generated On</div><div>${generatedOn}</div>
    <div>Total Records</div><div>${records.length}</div>
  </div>
  ${recordsHtml}
  <footer>For informational reference only. © ${new Date().getFullYear()} ${escapeHtml(BRAND_NAME)}.</footer>
</div>
</body>
</html>`;
}

export function triggerHtmlOpenAndDownload(htmlString, filename) {
  const safeName = filename || 'Kharedivikri_Report.html';
  const blobView = new Blob([htmlString], { type: 'text/html;charset=utf-8' });
  const blobDl = new Blob([htmlString], { type: 'text/html;charset=utf-8' });
  const viewUrl = URL.createObjectURL(blobView);
  const dlUrl = URL.createObjectURL(blobDl);
  try { window.open(viewUrl, '_blank', 'noopener,noreferrer'); } catch (_) {}
  try {
    const a = document.createElement('a');
    a.href = dlUrl;
    a.download = safeName;
    a.rel = 'noopener';
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } catch (_) {
    try { window.open(dlUrl, '_blank', 'noopener,noreferrer'); } catch (_) {}
  }
  setTimeout(() => {
    URL.revokeObjectURL(viewUrl);
    URL.revokeObjectURL(dlUrl);
  }, 60_000);
}

export function buildFullByKeysPayload(paymentToken, ctx, records) {
  const keys = records.map((r) => r && r.key).filter(Boolean);
  if (!paymentToken || keys.length === 0) return null;
  return ctx.d && ctx.t && ctx.v
    ? { token: paymentToken, d: ctx.d, t: ctx.t, v: ctx.v, query: ctx.query, keys }
    : {
        token: paymentToken,
        district: ctx.district,
        taluka: ctx.taluka,
        village: ctx.village,
        query: ctx.query,
        keys,
      };
}

export async function fetchFullPaidRecords(fullBody, { retries = 8 } = {}) {
  let lastRes = null;
  let lastMsg = '';
  for (let i = 0; i < retries; i++) {
    const fullRes = await fetch(`${API_BASE_URL}/search/full-by-keys`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(fullBody),
    });
    lastRes = fullRes;
    if (fullRes.ok) return fullRes;
    try {
      const errJson = await fullRes.json();
      lastMsg = String(errJson.error || '');
    } catch {
      lastMsg = '';
    }
    const pendingRace = fullRes.status === 403 && /not marked as paid/i.test(lastMsg);
    if (pendingRace && i < retries - 1) {
      await sleepMs(Math.min(200 * 1.65 ** i, 3500));
      continue;
    }
    break;
  }
  return lastRes;
}
